import { useMemo } from "react";

import { CHART_TYPE_DONUT, ChartBase } from "@datanac/datanac-visualization-toolkit/dist/Components/chart/ChartBase";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { formatNumber, formatPrice } from "../../utility/formatters";
import {
  REVENUE_LABEL_MISCELLANEOUS_PROGRAM_PAYMENTS,
  REVENUE_LABEL_OTHER_GOVERNMENT_PAYMENTS,
} from "../ArcPlc/FarmPaymentConstants";
import { REVENUE_LABEL_ARC_PLC_PAYMENTS } from "../Marketing/useRevenueReport";
import { YieldForecast } from "../Weather/YieldForecast";
import { DashboardStatsCards } from "./DashboardStatsCards";
import { DashBoardTableCard } from "./DashBoardTableCard";
import ProgressBar from "./ProgressBar";

export const CropOverview = ({
  revenueDetail,
  currentCrop,
  calculateFuturesOptionsHedgedTotal,
  yieldAgg,
  projectedPayment,
  isLoadingFarmPayments,
}) => {
  const specificCropData = useMemo(
    () => revenueDetail?.crops?.find((c) => c.name === currentCrop?.name),
    [revenueDetail, currentCrop]
  );

  const specificCropRevenueData = useMemo(() => specificCropData?.revenueData || [], [specificCropData]);

  const specificCropYieldData = useMemo(() => specificCropData?.yieldData || [], [specificCropData]);

  const specificCropMarketingData = useMemo(() => specificCropData?.marketingData || [], [specificCropData]);

  const specificCropInsuranceData = useMemo(() => specificCropData?.insuranceData || [], [specificCropData]);

  const protectedPercent = useMemo(() => {
    if (!specificCropYieldData?.length) return 0;

    const hedged = specificCropYieldData.find((item) => item.label === "Total Hedged Position")?.value || 0;
    const production = specificCropYieldData.find((item) => item.label === "Projected Production")?.value || 0;

    return production > 0 ? hedged / production : 0;
  }, [specificCropYieldData]);

  const cropYieldForecast = useMemo(
    () => yieldAgg?.byCommodity?.[currentCrop?.name] || null,
    [yieldAgg?.byCommodity, currentCrop?.name]
  );

  const cropProjectedPayment = useMemo(
    () => projectedPayment?.lookupByCommodity?.(currentCrop?.name) || null,
    [projectedPayment, currentCrop?.name]
  );

  const cropRevenueSummary = useMemo(
    () => [
      {
        label: "Forward Sales",
        value: formatPrice(specificCropRevenueData?.find((revenue) => revenue.label === "Sold Revenue")?.value || 0, 0),
      },
      {
        label: "Unsold Production",
        value: formatPrice(
          specificCropRevenueData?.find((revenue) => revenue.label === "Unsold Revenue")?.value || 0,
          0
        ),
      },
      ...(specificCropData?.cropBasis
        ? [
            { label: "Board Price", value: formatPrice(specificCropData.boardPrice, 4), indented: true },
            { label: "Basis", value: formatPrice(specificCropData.cropBasis, 4), indented: true },
          ]
        : []),
      {
        label: "Net Hedging Gain/Loss",
        value: formatPrice(
          (() => {
            try {
              const closedGainLoss =
                specificCropRevenueData?.find((revenue) => revenue.label === "Closed Gain/Loss")?.value || 0;
              const openGainLoss =
                specificCropRevenueData?.find((revenue) => revenue.label === "Open Gain/Loss")?.value || 0;
              return closedGainLoss + openGainLoss;
            } catch (error) {
              console.warn("Error calculating Net Hedging Gain/Loss:", error);
              return 0;
            }
          })(),
          0
        ),
      },
      {
        label: "Other Revenue",
        value: formatPrice(
          specificCropRevenueData?.find((revenue) => revenue.label === "Other Payments")?.value || 0,
          0
        ),
      },
      {
        label: "Total Revenue",
        value: formatPrice(
          specificCropRevenueData?.find((revenue) => /Total Revenue/i.test(revenue.label))?.value || 0,
          0
        ),
      },
      {
        label: "Crop Insurance Guarantee",
        value: formatPrice(
          specificCropInsuranceData?.find((insurance) => insurance.label === "Crop Insurance Guarantee")?.value || 0,
          0
        ),
        highlighted: true,
      },
      {
        label: REVENUE_LABEL_ARC_PLC_PAYMENTS,
        value: formatPrice(
          specificCropRevenueData?.find((revenue) => revenue.label === REVENUE_LABEL_ARC_PLC_PAYMENTS)?.value || 0,
          0
        ),
        highlighted: true,
      },
      {
        label: "Insurance Payment (projected)",
        value: projectedPayment?.isLoading
          ? "--"
          : formatPrice(cropProjectedPayment?.totalIndemnity, 0),
        tooltipText:
          projectedPayment?.isLoading || !currentCrop?.name
            ? undefined
            : (cropProjectedPayment?.breakdown || [])
                .map((c) => `${c.county_name} (${c.practice_name}) ${formatPrice(c.projectedIndemnity, 0)}`)
                .join("\n") || undefined,
        highlighted: true,
      },
      {
        label: REVENUE_LABEL_OTHER_GOVERNMENT_PAYMENTS,
        value: isLoadingFarmPayments
          ? "--"
          : formatPrice(
              specificCropRevenueData?.find((revenue) => revenue.label === REVENUE_LABEL_OTHER_GOVERNMENT_PAYMENTS)
                ?.value || 0,
              0
            ),
        highlighted: true,
      },
      {
        label: REVENUE_LABEL_MISCELLANEOUS_PROGRAM_PAYMENTS,
        value: isLoadingFarmPayments
          ? "--"
          : formatPrice(
              specificCropRevenueData?.find(
                (revenue) => revenue.label === REVENUE_LABEL_MISCELLANEOUS_PROGRAM_PAYMENTS
              )?.value || 0,
              0
            ),
        highlighted: true,
      },
    ],
    [
      specificCropRevenueData,
      specificCropInsuranceData,
      projectedPayment,
      cropProjectedPayment,
      currentCrop?.name,
      isLoadingFarmPayments,
    ]
  );

  const cropSalesSummary = useMemo(
    () => [
      {
        label: "Projected Yield",
        value: formatNumber(currentCrop?.rows?.find((row) => row.practiceName === "Total")?.totalYield, 0),
      },
      {
        label: "Forward Sales",
        value: formatNumber(specificCropYieldData?.find((revenue) => revenue.label === "Sold")?.value || 0, 0),
      },
      {
        label: "HTA",
        value: formatNumber(
          specificCropYieldData?.find((yieldItem) => yieldItem.label === "Sold")?.detailedUnits?.hta || 0
        ),
      },
      {
        label: "Futures and Options",
        value: formatNumber(calculateFuturesOptionsHedgedTotal(currentCrop?.name) || 0, 0),
      },
      // {
      //   label: "OTC",
      //   value: formatNumber(specificCropYieldData?.find((revenue) => revenue.label === "OTC")?.value || 0, 0),
      // },
      {
        label: "Unsold Production",
        value: formatNumber(specificCropYieldData?.find((revenue) => revenue.label === "Unsold")?.value || 0, 0),
      },
      {
        label: "Protected",
        value: formatNumber(specificCropYieldData?.find((yieldItem) => yieldItem.label === "Protected")?.value || 0, 0),
        highlighted: true,
      },
      {
        label: "Unprotected",
        value: specificCropMarketingData?.find((marketingItem) => marketingItem.label === "Unprotected")?.value || 0,
        highlighted: true,
      },
    ],
    [
      currentCrop,
      specificCropRevenueData,
      specificCropYieldData,
      specificCropMarketingData,
      calculateFuturesOptionsHedgedTotal,
      formatNumber,
      formatPrice,
    ]
  );

  const chartDataSalesTypeByCrop = useMemo(
    () => [
      {
        category: "Cash",
        id: 1,
        dataValueY_mean:
          specificCropYieldData?.find((yieldItem) => yieldItem.label === "Sold")?.detailedUnits?.cash || 0,
      },
      {
        category: "HTA",
        id: 2,
        dataValueY_mean:
          specificCropYieldData?.find((yieldItem) => yieldItem.label === "Sold")?.detailedUnits?.hta || 0,
      },
      {
        category: "Futures & Options",
        id: 3,
        dataValueY_mean: calculateFuturesOptionsHedgedTotal(currentCrop?.name) || 0,
      },
      {
        category: "Basis",
        id: 4,
        dataValueY_mean:
          specificCropYieldData?.find((yieldItem) => yieldItem.label === "Sold")?.detailedUnits?.basis || 0,
      },
      {
        category: "No Price Established",
        id: 5,
        dataValueY_mean:
          specificCropYieldData?.find((yieldItem) => yieldItem.label === "Sold")?.detailedUnits?.noPriceEstablished ||
          0,
      },
      // {
      //   category: "OTC",
      //   id: 4,
      //   dataValueY_mean:
      //     specificCropYieldData?.find((yieldItem) => yieldItem.label === "Sold")?.detailedUnits?.otc || 0,
      // },
      {
        category: "Unprotected",
        id: 5,
        dataValueY_mean:
          specificCropYieldData?.find((yieldItem) => yieldItem.label === "Sold")?.detailedUnits?.unprotected || 0,
      },
    ],
    [currentCrop, specificCropYieldData, calculateFuturesOptionsHedgedTotal]
  );

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          mb: 2,
          "@media print": {
            marginBottom: 0,
          },
        }}
      >
        <Box>
          {/* Add Summary Cards */}
          <DashboardStatsCards revenueDetail={revenueDetail} currentCrop={currentCrop} isCropSpecific={true} />
          {cropYieldForecast && (
            <Card
              sx={{
                boxShadow: "none",
                borderRadius: "14px",
                border: "1px solid",
                borderColor: "grey.200",
                backgroundColor: "grey.100",
                mb: 2,
              }}
            >
              <CardContent>
                <YieldForecast
                  selectedCropType={currentCrop?.name}
                  isLoadingYieldForecast={yieldAgg?.isLoading}
                  isLoadingCountyData={yieldAgg?.isLoading}
                  isLoadingHistoricalYieldTrend={yieldAgg?.isLoading}
                  yieldForecastValues={{
                    irrigated: cropYieldForecast.weightedDatanacIrrigated,
                    nonIrrigated: cropYieldForecast.weightedDatanacNonIrrigated,
                  }}
                  rmaProjectedYieldIrrigated={cropYieldForecast.weightedRmaProjectedIrrigated}
                  rmaProjectedYieldNonIrrigated={cropYieldForecast.weightedRmaProjectedNonIrrigated}
                  rmaHistoricalYieldIrrigated={cropYieldForecast.weightedRmaHistoricalIrrigated}
                  rmaHistoricalYieldNonIrrigated={cropYieldForecast.weightedRmaHistoricalNonIrrigated}
                />
              </CardContent>
            </Card>
          )}
          <Grid
            container
            spacing={2}
            sx={{
              mb: 2,
              "@media print": {
                paddingTop: "20px",
              },
            }}
          >
            {/* Revenue Summary */}
            <Grid
              item
              xs={12}
              lg={6}
              xl={4}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: ".75rem",
                "@media print": {
                  flexBasis: "50%",
                  maxWidth: "50%",
                  width: "50%",
                  order: 1,
                  margin: 0,
                  padding: "0 10px",
                  boxSizing: "border-box",
                },
              }}
              className="revenue-dashboard-card"
            >
              <Typography
                className="revenue-title"
                sx={{
                  color: "var(--mui-palette-primary-dark)",
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  "@media print": {
                    fontSize: "0.85rem !important",
                    lineHeight: "1.2",
                    marginBottom: "0.5rem",
                  },
                }}
              >
                Revenue summary
              </Typography>
              <DashBoardTableCard data={cropRevenueSummary} />
            </Grid>

            {/* Crops Sold and Hedged + Sales Type */}
            <Grid
              item
              xs={12}
              xl={4}
              sx={{
                order: { lg: 3, xl: 2 },
                display: "flex",
                flexDirection: "column",
                gap: ".75rem",
                "@media print": {
                  order: 3,
                  margin: 0,
                  padding: "0 10px",
                  boxSizing: "border-box",
                },
              }}
              className="revenue-dashboard-card"
            >
              {/* Top block: % of crops sold and hedged */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
                <Typography
                  className="revenue-title"
                  sx={{
                    color: "var(--mui-palette-primary-dark)",
                    fontSize: "1.5rem",
                    fontWeight: "700",
                    "@media print": {
                      fontSize: "0.85rem !important",
                      lineHeight: "1.2",
                      marginBottom: "0.5rem",
                    },
                  }}
                >
                  % of crops sold and hedged
                </Typography>
                <Card sx={{ boxShadow: "none", borderRadius: "14px", width: "100%", marginBottom: 2 }}>
                  <CardContent>
                    <ProgressBar value={Math.round(protectedPercent * 100)} />
                  </CardContent>
                </Card>
              </Box>
              {/* Spacer to push the next block to the bottom */}
              <Box sx={{ flexGrow: { xs: 0, lg: 1 } }} />
              {/* Bottom block: % of sales type */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: ".75rem" }} className="revenue-dashboard-card">
                <Typography
                  className="revenue-title"
                  sx={{
                    color: "var(--mui-palette-primary-dark)",
                    fontSize: "1.5rem",
                    fontWeight: "700",
                    "@media print": {
                      fontSize: "0.85rem !important",
                      lineHeight: "1.2",
                      marginBottom: "0.5rem",
                    },
                  }}
                >
                  % of sales type
                </Typography>
                <Card
                  sx={{
                    boxShadow: "none",
                    borderRadius: "14px",
                    "@media print": {
                      padding: "0px",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      "@media print": {
                        padding: "0px",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        display: { xs: "flex", sm: "none" },
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <ChartBase
                        data={chartDataSalesTypeByCrop}
                        type={CHART_TYPE_DONUT}
                        size="small"
                        allowedChartTypes={[CHART_TYPE_DONUT]}
                        isShowChartSelection={false}
                        width={320}
                        height={230}
                        showLegend={true}
                        scaleColors={[
                          theme.palette.colors.textVibrantGreen, // Green $$ (vibrant)
                          theme.palette.colors.textDarkGreen, // Green $ (slightly darker)
                          theme.palette.colors.textGraySlate, // Gray
                          theme.palette.colors.textWarningYellow, // Yellow
                          theme.palette.colors.textOrangeAccent, // Orange
                          theme.palette.colors.textDangerRed, // Red
                        ]}
                        legendDirection={"vertical"}
                        legendPosition="right"
                        legendFontSize={16}
                        renderer="svg"
                      />
                    </Box>
                    <Box
                      sx={{
                        height: "100%",
                        display: { xs: "none", sm: "flex" },
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <ChartBase
                        data={chartDataSalesTypeByCrop}
                        type={CHART_TYPE_DONUT}
                        size="small"
                        allowedChartTypes={[CHART_TYPE_DONUT]}
                        isShowChartSelection={false}
                        width={340}
                        height={220}
                        showLegend={true}
                        scaleColors={[
                          theme.palette.colors.textVibrantGreen, // Green $$ (vibrant)
                          theme.palette.colors.textDarkGreen, // Green $ (slightly darker)
                          theme.palette.colors.textGraySlate, // Gray
                          theme.palette.colors.textWarningYellow, // Yellow
                          theme.palette.colors.textOrangeAccent, // Orange
                          theme.palette.colors.textDangerRed, // Red
                        ]}
                        legendDirection={"vertical"}
                        legendPosition="right"
                        legendFontSize={16}
                        renderer="svg"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>

            {/* Crop Sales Summary */}
            <Grid
              item
              xs={12}
              lg={6}
              xl={4}
              sx={{
                order: {
                  lg: 2,
                  xl: 3,
                },
                display: "flex",
                gap: ".75rem",
                flexDirection: "column",
                "@media print": {
                  flexBasis: "50%",
                  maxWidth: "50%",
                  width: "50%",
                  order: 2,
                  margin: 0,
                  padding: "0 10px",
                  boxSizing: "border-box",
                },
              }}
              className="revenue-dashboard-card"
            >
              <Typography
                className="revenue-title"
                sx={{
                  color: "var(--mui-palette-primary-dark)",
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  "@media print": {
                    fontSize: "0.85rem !important",
                    lineHeight: "1.2",
                    marginBottom: "0.5rem",
                  },
                }}
              >
                Crop sales summary
              </Typography>
              <DashBoardTableCard data={cropSalesSummary} />
            </Grid>
          </Grid>

          {/* Crop Table Grid Item */}
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{
                "@media print": {
                  order: 4,
                  margin: 0,
                  padding: "0 10px",
                  boxSizing: "border-box",
                },
              }}
            >
              {isMobile ? (
                <Card
                  sx={{
                    width: "100%",
                    boxShadow: "none",
                    borderRadius: "14px",
                    "@media print": {
                      boxShadow: "none",
                      borderRadius: "8px",
                      margin: 0,
                      padding: "4px",
                      height: "100%",
                    },
                  }}
                  className="revenue-dashboard-card"
                >
                  <CardContent
                    sx={{
                      "@media print": {
                        padding: "4px",
                        margin: 0,
                        height: "100%",
                      },
                    }}
                  >
                    {currentCrop?.rows.map((row, id) => (
                      <Box
                        key={`${currentCrop.name}-${id}`}
                        sx={{
                          mb: id === currentCrop.rows.length - 1 ? 0 : 4,
                          "@media print": {
                            mb: id === currentCrop.rows.length - 1 ? 0 : 2,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            "@media print": {
                              mb: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              width: "40%",
                              fontSize: "0.875rem",
                              textTransform: "capitalize",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            Practice
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              width: "60%",
                              fontWeight: row.practiceName === "Total" ? "600" : "500",
                              fontSize: "0.875rem",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            {row.practiceName}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            "@media print": {
                              mb: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              width: "40%",
                              fontSize: "0.875rem",
                              textTransform: "capitalize",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            Acres
                          </Typography>
                          <Typography
                            sx={{
                              width: "60%",
                              fontSize: "0.875rem",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            {formatNumber(row.acres, 0)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            "@media print": {
                              mb: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              width: "40%",
                              fontSize: "0.875rem",
                              textTransform: "capitalize",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            Cost/Acre
                          </Typography>
                          <Typography
                            sx={{
                              width: "60%",
                              fontSize: "0.875rem",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            {formatPrice(row.costPerAcre, 0)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            "@media print": {
                              mb: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              width: "40%",
                              fontSize: "0.875rem",
                              textTransform: "capitalize",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            Total Cost
                          </Typography>
                          <Typography
                            sx={{
                              width: "60%",
                              fontSize: "0.875rem",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            {formatPrice(row.totalCost, 0)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            "@media print": {
                              mb: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              width: "40%",
                              fontSize: "0.875rem",
                              textTransform: "capitalize",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            Projected Yield/Acre
                          </Typography>
                          <Typography
                            sx={{
                              width: "60%",
                              fontSize: "0.875rem",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            {formatNumber(row.projectedYieldPerAcre, 2)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            "@media print": {
                              mb: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              width: "40%",
                              fontSize: "0.875rem",
                              textTransform: "capitalize",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            Total Yield
                          </Typography>
                          <Typography
                            sx={{
                              width: "60%",
                              fontSize: "0.875rem",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            {formatNumber(row.totalYield, 2)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              width: "40%",
                              fontSize: "0.875rem",
                              textTransform: "capitalize",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            Break Even
                          </Typography>
                          <Typography
                            sx={{
                              width: "60%",
                              fontSize: "0.875rem",
                              "@media print": {
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            {formatPrice(row.breakEven, 4)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              ) : (
                <Card
                  sx={{
                    boxShadow: "none",
                    borderRadius: "14px",
                    width: "100%",
                    "@media print": {
                      width: "100%",
                      boxShadow: "none",
                      borderRadius: "8px",
                      margin: 0,
                      padding: "4px",
                      pageBreakAfter: "avoid",
                      breakAfter: "avoid",
                    },
                  }}
                  className="revenue-dashboard-card"
                >
                  <CardContent
                    sx={{
                      width: "100%",
                      "@media print": {
                        padding: "4px",
                        margin: 0,
                      },
                    }}
                  >
                    <TableContainer
                      sx={{
                        boxShadow: "none",
                        maxWidth: "100%",
                        overflowX: "auto",
                        overflowY: "hidden",
                        "@media print": {
                          overflow: "visible",
                        },
                      }}
                    >
                      <Table
                        size="small"
                        sx={{
                          width: "100%",
                          pageBreakInside: "avoid",
                          "@media print": {
                            fontSize: "0.75rem",
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow sx={{ width: "100%" }}>
                            <TableCell
                              sx={{
                                bgcolor: "var(--mui-palette-secondary-dark)",
                                color: "common.white",
                                fontWeight: "bold",
                                borderTopLeftRadius: "20px",
                                borderBottomLeftRadius: "20px",
                                whiteSpace: "nowrap",
                                border: 0,
                                "@media print": {
                                  fontSize: "0.75rem",
                                  padding: "4px",
                                  bgcolor: "transparent",
                                  color: "text.primary",
                                },
                              }}
                            >
                              Practice
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                bgcolor: "var(--mui-palette-secondary-dark)",
                                color: "common.white",
                                fontWeight: "bold",
                                border: 0,
                                "@media print": {
                                  fontSize: "0.75rem",
                                  padding: "4px",
                                  bgcolor: "transparent",
                                  color: "text.primary",
                                },
                              }}
                            >
                              Acres
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                bgcolor: "var(--mui-palette-secondary-dark)",
                                color: "common.white",
                                fontWeight: "bold",
                                border: 0,
                                "@media print": {
                                  fontSize: "0.75rem",
                                  padding: "4px",
                                  bgcolor: "transparent",
                                  color: "text.primary",
                                },
                              }}
                            >
                              Cost/Acre
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                bgcolor: "var(--mui-palette-secondary-dark)",
                                color: "common.white",
                                fontWeight: "bold",
                                border: 0,
                                "@media print": {
                                  fontSize: "0.75rem",
                                  padding: "4px",
                                  bgcolor: "transparent",
                                  color: "text.primary",
                                },
                              }}
                            >
                              Total Cost
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                bgcolor: "var(--mui-palette-secondary-dark)",
                                color: "common.white",
                                fontWeight: "bold",
                                border: 0,
                                "@media print": {
                                  fontSize: "0.75rem",
                                  padding: "4px",
                                  bgcolor: "transparent",
                                  color: "text.primary",
                                },
                              }}
                            >
                              Yield
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                bgcolor: "var(--mui-palette-secondary-dark)",
                                color: "common.white",
                                fontWeight: "bold",
                                border: 0,
                                "@media print": {
                                  fontSize: "0.75rem",
                                  padding: "4px",
                                  bgcolor: "transparent",
                                  color: "text.primary",
                                },
                              }}
                            >
                              Production
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                bgcolor: "var(--mui-palette-secondary-dark)",
                                color: "common.white",
                                fontWeight: "bold",
                                borderTopRightRadius: "20px",
                                borderBottomRightRadius: "20px",
                                border: 0,
                                "@media print": {
                                  fontSize: "0.75rem",
                                  padding: "4px",
                                  bgcolor: "transparent",
                                  color: "text.primary",
                                },
                              }}
                            >
                              Break Even
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {currentCrop?.rows.map((row, id, arr) => {
                            const isTotal = row.practiceName === "Total";
                            const styleCell = {
                              fontWeight: isTotal ? "bold" : "normal",
                              fontSize: isTotal ? "1rem" : "0.875rem",
                              whiteSpace: "nowrap",
                              borderBottom: id === arr.length - 1 ? "none" : undefined,
                              paddingTop: 1.5,
                              paddingBottom: isTotal ? 0 : 1.5,
                              "@media print": {
                                fontSize: "0.75rem",
                                padding: "4px",
                                fontWeight: isTotal ? "bold" : "normal",
                              },
                            };
                            return (
                              <TableRow key={`${currentCrop.name}-${id}`}>
                                <TableCell sx={styleCell}>{row.practiceName}</TableCell>
                                <TableCell align="right" sx={styleCell}>
                                  {formatNumber(row.acres, 0)}
                                </TableCell>
                                <TableCell align="right" sx={styleCell}>
                                  {formatPrice(row.costPerAcre, 0)}
                                </TableCell>
                                <TableCell align="right" sx={styleCell}>
                                  {formatPrice(row.totalCost, 0)}
                                </TableCell>
                                <TableCell align="right" sx={styleCell}>
                                  {formatNumber(row.projectedYieldPerAcre, 2)}
                                </TableCell>
                                <TableCell align="right" sx={styleCell}>
                                  {formatNumber(row.totalYield, 2)}
                                </TableCell>
                                <TableCell align="right" sx={styleCell}>
                                  {formatPrice(row.breakEven, 4)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};
